import { conform, useForm } from "@conform-to/react";
import { getFieldsetConstraint, parse } from "@conform-to/zod";
import { json, type DataFunctionArgs } from "@remix-run/node";
import { Form, useLoaderData } from "@remix-run/react";
import { useEffect } from "react";
import { AuthenticityTokenInput } from "remix-utils/csrf/react";
import { z } from "zod";
import { TextareaField } from "#app/components/forms";
import { StatusButton } from "#app/components/ui/status-button";
import { validateCSRF } from "#app/utils/csrf.server";
import { useIsPending } from "#app/utils/misc";
import {openai, assistantId} from "#app/utils/openai.server";
import {threadSession} from "#app/utils/session.server";

const MIN_LENGTH = 1;
const MAX_LENGTH= 10000;
const ThreadSchema = z.object({
    threadId: z.string(),
    question: z.string().min(MIN_LENGTH).max(MAX_LENGTH).optional()
});

export async function loader({request}: DataFunctionArgs) {
    const session = await threadSession.getSession(request.headers.get("Cookie"));
    let threadId : (string | null) = session.get("threadId");
    let cookie = "";
    if (!threadId) {
        const thread = await openai.beta.threads.create();
        threadId = thread.id;
        session.set("threadId", threadId);
        cookie = await threadSession.commitSession(session);
    }
    
    const messages = await openai.beta.threads.messages.list(threadId);
    const chatMessages = messages.data.reverse().map((msg) => msg.content[0].text.value).reduce((r, element, index) => {
        index % 2 === 0 && r.push([]);
        r[r.length - 1].push(element);
        return r;
    }, []);
    // console.log(chatMessages);

    return json(
        {threadId, chatMessages},
        {
            headers: {
                "Set-Cookie": cookie
            }
        }
    );
}

export async function action({request}: DataFunctionArgs) {
    const formData = await request.formData();
    await validateCSRF(formData, request.headers);
    const result = await parse(formData, {
        schema: ThreadSchema,
        async: true
    });
    const {question, threadId} = result.value!;

    let answer : (string | null) = null;
    if (question && threadId) {
        await openai.beta.threads.messages.create(threadId, {
            role: "user",
            content: question
        });

        // const run = await openai.beta.threads.runs.create(threadId, {assistant_id: assistantId!});
        // let runStatus = await openai.beta.threads.runs.retrieve(threadId, run.id);
        // while (runStatus.status !== "completed") {
        //     await new Promise((resolve) => setTimeout(resolve, 500));
        //     runStatus = await openai.beta.threads.runs.retrieve(threadId, run.id);
        // }
        // const messages = await openai.beta.threads.messages.list(threadId);
        // const lastMessage = messages.data.filter((msg) => msg.run_id === run.id && msg.role === "assistant").pop();
        // answer = lastMessage?.content[0].text.value;
        const run = await openai.beta.threads.runs.createAndPoll(threadId, {
            assistant_id: assistantId!
        });
        const messages = await openai.beta.threads.messages.list(threadId, {run_id: run.id});
        const lastMessage = messages?.data?.pop();
        answer = lastMessage?.content[0].text.value;
    
        return json({question, answer});
    }
    
    return null;
}

export default function ChatRoute(){
    const loaderData = useLoaderData<typeof loader>();
    const isPending = useIsPending();

    const [form, fields] = useForm({
        id: "thread-input",
        constraint: getFieldsetConstraint(ThreadSchema),
        onValidate({formData}) {
            return parse(formData, {schema: ThreadSchema});
        },
        defaultValue: {
            question: ""
        }
    });

    useEffect(() => {
        form.props.ref?.current?.reset();
    }, [isPending]);

    return (
        <main className="container flex h-full min-h-[400px] px-0 pb-12 md:px-8">
            <div className="flex flex-col">
                <div className="flex flex-col gap-3 text-center mx-auto">
                    <h1 className="mx-6 text-base text-secondary font-semibold md:text-lg text-left lg:text-2xl">
                        Welcome back. I'm here to help you improve your poetry. Let's begin ...
                    </h1>
                    {loaderData.chatMessages && (
                        loaderData.chatMessages.map((message: string[]) => (
                            <>
                                <p className="text-left text-muted-foreground mx-8 italic whitespace-pre-line ">
                                    User: {message[0]}
                                </p>
                                <p className="text-left text-secondary mx-8 whitespace-pre-line">
                                    AI: {message[1]}
                                </p>
                            </>
                        ))
                    )}
                </div>
                <Form
                    method="POST"
                    className="flex h-full flex-col gap-y-4 overflow-y-auto overflow-x-hidden px-10 pb-28 pt-12"
                    {...form.props}
                >
                    <AuthenticityTokenInput />
                    <button type="submit" className="hidden" />
                    <input className="hidden" name="threadId" defaultValue={loaderData.threadId} />
                    <div className="flex gap-4 items-center">
                        <TextareaField
                            labelProps={{children: ""}}
                            textareaProps={{
                                autoFocus: true,
                                ...conform.textarea(fields.question, {ariaAttributes: true})
                            }}
                            className="w-full"
                            errors={fields.question.errors}
                        />
                        <StatusButton 
                            type="submit" 
                            variant="secondary"
                            className="p-6"
                            form={form.id}
                            // name="intent"
                            // value="chat"
                            disabled={isPending}
                            status={isPending ? "pending" : "idle"}
                        >
                            Chat
                        </StatusButton>
                    </div>
                </Form>
            </div>
        </main>
    );
}